/** Shared execution and eligibility for the Visit-scoped planned-item row menu. */
import { inject } from '@angular/core';
import { ActivityEntityName, JobSecurityRight, PlannedItemCommandId, PlannedItemLine, PlannedItemScope, VisitTaskChange } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { EntityDataService } from '@workbuddy/services/entities';
import { Command } from '@workbuddy/ui/model';

/** Menu wording and icon per action. Removal is the only destructive one, so it alone carries the bin. */
const PLANNED_ITEM_ACTIONS: Readonly<Record<PlannedItemCommandId, { label: string; iconLeft: string }>> = {
  [PlannedItemCommandId.Exclude]: { label: 'Exclude from this visit', iconLeft: 'fa-regular fa-circle-minus' },
  [PlannedItemCommandId.Restore]: { label: 'Restore', iconLeft: 'fa-regular fa-arrow-rotate-left' },
  [PlannedItemCommandId.Remove]: { label: 'Remove from this visit', iconLeft: 'fa-regular fa-trash-can' },
};

/**
 * Items follow the task model: excluding is never destructive, so every line — inherited or added —
 * can be excluded and brought back.
 *
 * - Exclude: any line that is not already excluded.
 * - Restore: an excluded line, undoing only the exclusion. An inherited line returns to its source
 *   defaults, an added line returns to Added for this visit. Also undoes a plain modification.
 * - Remove: a line added for this visit that is already excluded. This is the only permanent delete,
 *   and it is deliberately unreachable until the user has excluded the line first.
 *
 * "Added for this visit" is read off `scope`, not off `visitChange`. `visitChange` is a single value
 * and an added line that has since been excluded reports `Excluded` — the very state Remove gates on —
 * so it cannot also report `Added`. A line the Visit added stands alone rather than pointing at a
 * standing line, which is what its non-standing `scope` records; an inherited line keeps the standing
 * scope it resolved from. If the server later gives the line an explicit "added" flag, read that here.
 */
function isPlannedItemActionAllowed(commandId: PlannedItemCommandId, line: PlannedItemLine): boolean {

  const excluded = line.visitChange === VisitTaskChange.Excluded;
  const addedForThisVisit = line.scope !== PlannedItemScope.Standing;

  if (commandId === PlannedItemCommandId.Exclude) return !excluded;
  if (commandId === PlannedItemCommandId.Remove) return excluded && addedForThisVisit;
  return !!line.exceptionId && line.visitChange !== VisitTaskChange.Added;
}

export function plannedItemRowAction(commandId: PlannedItemCommandId, line: PlannedItemLine, onSaved?: () => void): Command {

  const data = inject(EntityDataService);
  const rights = inject(RightsService);
  const allowed = !line.readonly
    && rights.hasRights(JobSecurityRight.Manage)
    && isPlannedItemActionAllowed(commandId, line);

  return new Command({
    _id: commandId,
    ...PLANNED_ITEM_ACTIONS[commandId],
    hidden: !allowed,
    action: async () => {
      if (!allowed) return;
      await data.executeCommand(ActivityEntityName.PlannedItem, commandId, { id: line._id });
      data.invalidate(ActivityEntityName.PlannedItem);
      data.invalidateItems(ActivityEntityName.Visit);
      onSaved?.();
    },
  });
}
